import { Injectable } from '@angular/core';
import { Firestore, Query, collection, query, where, DocumentData } from '@angular/fire/firestore';
import { AuthService } from './auth.service';
import { ChildAuthService } from './child-auth.service';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class FirestoreUtilsService {
  constructor(
    private firestore: Firestore,
    private authService: AuthService,
    private childAuthService: ChildAuthService
  ) {}

  /**
   * Creates a Firestore query that works correctly in both parent and child modes
   * This ensures all queries conform to the security rules by always using the parent's UID
   * for the parentId filter, regardless of whether the app is in child mode.
   * 
   * @param collectionPath The collection path to query
   * @param parentIdField The field name for the parent ID (defaults to 'parentId')
   * @returns A Query object with the correct parentId filter applied
   */
  async createParentScopedQuery(collectionPath: string, parentIdField: string = 'parentId'): Promise<Query<DocumentData>> {
    const collectionRef = collection(this.firestore, collectionPath);
    
    // Always use the parent's UID for security rule compliance
    const authUser = await this.authService.getCurrentUser();
    if (!authUser) {
      console.error(`[FirestoreUtilsService] No authenticated user found when creating query for ${collectionPath}`);
      throw new Error('No authenticated user found');
    }
    
    const parentId = authUser.uid;
    console.log(`[FirestoreUtilsService] Creating query for ${collectionPath} with ${parentIdField}=${parentId}`);
    
    // Log child mode status for debugging
    const childState = await firstValueFrom(this.childAuthService.getAuthState());
    console.log(`[FirestoreUtilsService] Child mode: ${childState.isChildMode}, Child ID: ${childState.loggedInChild?.id || 'none'}`);
    
    // Create a query with the parentId filter
    return query(
      collectionRef,
      where(parentIdField, '==', parentId)
    );
  }

  /**
   * Gets the currently active child ID if in child mode, otherwise returns null
   * Useful for client-side filtering after queries
   */
  async getActiveChildId(): Promise<string | null> {
    const childState = await firstValueFrom(this.childAuthService.getAuthState());
    return (childState.isChildMode && childState.loggedInChild) ? childState.loggedInChild.id : null;
  }
}
